/**
 * AML API client.
 *
 *   GET /get-aml/:customer_id     -> getAmlList
 *   GET /cibil/view-aml/:id       -> getAmlView
 */
import type { AmlListRow } from "../lib/aml/types";
import type {
  ScoremeDetailsData,
  ScoremeDetailsResponse,
} from "../lib/aml/scoremeDetails";
import apiService from "./apiService";

function asRow(item: unknown): AmlListRow | null {
  if (!item || typeof item !== "object") return null;
  const raw = item as Record<string, unknown>;
  const directorUuid =
    raw.director_uuid ?? raw.directors_uuid ?? raw.uuid ?? null;
  const totalLegalCases =
    raw.total_legal_cases ?? raw.totalLegalCases ?? raw.legal_cases_count ?? null;
  const isLegalFetch =
    raw.is_legal_fetch ?? raw.isLegalFetch ?? raw.legal_fetch ?? null;

  return {
    ...(raw as unknown as AmlListRow),
    director_uuid:
      directorUuid === null || directorUuid === undefined
        ? null
        : String(directorUuid),
    total_legal_cases:
      totalLegalCases === null || totalLegalCases === undefined
        ? null
        : (totalLegalCases as number | string),
    is_legal_fetch:
      isLegalFetch === null || isLegalFetch === undefined
        ? null
        : String(isLegalFetch),
  };
}

function unwrapList(payload: unknown): AmlListRow[] {
  let list: unknown[] = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (payload && typeof payload === "object" && "data" in payload) {
    const data = (payload as { data: unknown }).data;
    if (Array.isArray(data)) list = data;
  }
  return list.map(asRow).filter((row): row is AmlListRow => Boolean(row));
}

/** GET /get-aml/:customer_id — company + director AML summary rows */
export async function getAmlList(customerId: string): Promise<AmlListRow[]> {
  const res = await apiService.get<unknown>(`/cibil/get-aml/${customerId}`);
  return unwrapList(res);
}

/**
 * GET /cibil/view-aml/:id — full vendor AML JSON for one list row.
 * Returns the raw body (may be `{ status, message, data }` or the AML payload itself).
 */
export async function getAmlView(id: string | number): Promise<unknown> {
  return apiService.get<unknown>(`/cibil/view-aml/${id}`);
}

/** POST /scoreme/reference — trigger legal fetch for customer or a single director */
export async function fetchScoremeReference(payload: {
  customer_id: string;
  director_uuid: string;
}): Promise<unknown> {
  return apiService.post<unknown>(`/scoreme/reference`, payload);
}

/** GET /scoreme/details/:director_uuid — yearly legal case breakdown */
export async function getScoremeDetails(
  directorUuid: string
): Promise<ScoremeDetailsData> {
  const res = await apiService.get<ScoremeDetailsResponse>(
    `/scoreme/details/${encodeURIComponent(directorUuid)}`
  );
  return res.data;
}
